import { discountPercent, effectivePrice, type Product } from '@/lib/catalogue';
import { cn, formatMoney } from '@/lib/utils';

/**
 * Carte produit — pensée pour un doigt, debout, sur un iPad.
 * La zone principale est cliquable (cible d'au moins 96 px de haut) plutôt qu'un
 * petit bouton « ajouter » qu'on rate une fois sur trois. Un produit VEDETTE porte
 * un badge (lecture seule) ; si `onToggleFeatured` est fourni (admin), une étoile
 * permet de le marquer / démarquer vedette.
 */
export default function ProductCard({
  product,
  onSelect,
  onToggleFeatured,
  view = 'grid',
}: {
  product: Product;
  onSelect?: (p: Product) => void;
  onToggleFeatured?: (p: Product) => void;
  /** 'grid' : grande carte tactile. 'list' : ligne compacte, plus dense. */
  view?: 'grid' | 'list';
}) {
  const remise = discountPercent(product);

  // Vue liste : une seule ligne par produit, pour voir beaucoup plus d'articles à
  // la fois. Le conteneur (dans Catalogue) fournit la bordure et le fond ; ici on
  // ne pose qu'un séparateur et un survol.
  if (view === 'list') {
    return (
      <div className="hover:bg-accent flex w-full items-center transition-colors">
        <button
          type="button"
          onClick={() => onSelect?.(product)}
          className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left"
        >
          <span className="bg-secondary text-secondary-foreground shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] font-medium">
            {product.lineCode}
          </span>
          <span className="shrink-0 font-mono text-sm font-semibold">{product.partNumber}</span>
          {product.isFeatured && !onToggleFeatured && (
            <span className="shrink-0 text-amber-500" title="Vedette" aria-label="Vedette">
              ★
            </span>
          )}
          <span className="text-foreground min-w-0 flex-1 truncate text-sm">
            {product.description}
          </span>
          {remise !== null && (
            <span className="text-muted-foreground hidden text-xs line-through tabular-nums sm:inline">
              {formatMoney(product.listPrice)}
            </span>
          )}
          {remise !== null && (
            <span className="shrink-0 rounded bg-green-600/15 px-1.5 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">
              −{remise} %
            </span>
          )}
          <span className="w-24 shrink-0 text-right font-bold tabular-nums">
            {formatMoney(effectivePrice(product))}
          </span>
        </button>
        {onToggleFeatured && <FeaturedStar product={product} onToggle={onToggleFeatured} />}
      </div>
    );
  }

  return (
    <div className="bg-card hover:border-primary/50 flex min-h-24 w-full items-stretch overflow-hidden rounded-xl border transition-colors">
      <button
        type="button"
        onClick={() => onSelect?.(product)}
        className="active:bg-accent flex min-w-0 flex-1 items-center gap-4 p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
              {product.lineCode}
            </span>
            <span className="font-mono text-sm font-semibold">{product.partNumber}</span>
            {product.isFeatured && !onToggleFeatured && (
              <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                Vedette
              </span>
            )}
          </div>

          <p className="text-base leading-snug font-medium">{product.description}</p>

          <p className="text-muted-foreground mt-1 text-sm">
            {product.uom}
            {product.packQty > 1 && ` · paquet de ${product.packQty}`}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold tabular-nums">
            {formatMoney(effectivePrice(product))}
          </div>
          {remise !== null && (
            <div className="mt-0.5 flex items-center justify-end gap-2">
              {/* Prix liste barré : le rep doit pouvoir montrer l'économie au client. */}
              <span className="text-muted-foreground text-sm line-through tabular-nums">
                {formatMoney(product.listPrice)}
              </span>
              <span className="rounded bg-green-600/15 px-1.5 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">
                −{remise} %
              </span>
            </div>
          )}
        </div>
      </button>
      {onToggleFeatured && <FeaturedStar product={product} onToggle={onToggleFeatured} />}
    </div>
  );
}

/** Étoile vedette (admin) — bouton séparé (jamais imbriqué dans la zone cliquable). */
function FeaturedStar({ product, onToggle }: { product: Product; onToggle: (p: Product) => void }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(product)}
      aria-pressed={product.isFeatured}
      aria-label={product.isFeatured ? 'Retirer des vedettes' : 'Mettre en vedette'}
      title={product.isFeatured ? 'Retirer des vedettes' : 'Mettre en vedette'}
      className={cn(
        'flex shrink-0 items-center self-stretch px-4 text-2xl leading-none transition-colors',
        product.isFeatured ? 'text-amber-500' : 'text-muted-foreground/40 hover:text-amber-500',
      )}
    >
      {product.isFeatured ? '★' : '☆'}
    </button>
  );
}
